import { useState } from "react"
import { NavLink, useLocation } from "react-router-dom"
import { Popover } from "antd"

import { IMainPageUI } from "./types"
import { StorageWarningNav } from './storage-warning-nav'
import { OcrSettingsLink } from '@pages/settings/ocr/navigation'
import MainRoutes from "@routes/main.routes"

import { useWindowSize } from "@hooks/useWindowSize"
import { useDeviceType } from "@hooks/useDeviceType"

import { 
    IconApplications, IconContracts, IconStatistics, IconDelivery, 
    IconEntity, IconExitApp, IconJobs, IconKP, 
    IconListeners, IconMenu, IconProfile, 
    IconServer, 
    IconSettings, IconSignature,
    IconUsers,
    IconArchive, IconDiplom,
    IconPayment,
    IconAddress
} from "@img/icons"

import { Badge, Container, Divider, Flex, ToolTip } from "@ui/index"
import { PageHeaderPortalContext } from "@primitive/sectionHeadings"

import css from './ui.module.scss'


/** UI - Главная страница */
export const MainPageUI = ({
    sectioName, 
    countNewApplications, countDelayedActionExpiredApplications, countUnverifiedWorks,
    storageWarnings,
    handleOpenDrawerMenu, handleOpenModalConfirm
}: IMainPageUI) => {
    const location = useLocation()
    const windowSize = useWindowSize()
    const deviceType = useDeviceType()
    const [pageHeaderPortalTarget, setPageHeaderPortalTarget] = useState<HTMLDivElement | null>(null)

    const isSettingsPage = ['/lk/users', '/lk/systems', '/lk/ocr'].some(path =>
        location.pathname.startsWith(path)
    )
    const isFinalExaminationPage = ['/lk/finalExamination', '/lk/archive', '/lk/prints'].some(path =>
        location.pathname.startsWith(path)
    )
    const isDeliveryPage = ['/lk/finality', '/lk/paymentNotifications', '/lk/addressNotConfirmed'].some(path =>
        location.pathname.startsWith(path)
    )

    const getNavFooter = () => {
        const popoverSettingsContent = (
            <Flex gap="8px">
                <ToolTip placement="right" title='Пользователи'>
                    <NavLink to='/lk/users' className={({ isActive }) => isActive ? `${css.nav_button} ${css.nav_button_active}` : `${css.nav_button}`}>
                        <IconUsers width='20px' height='20px' />
                    </NavLink>
                </ToolTip>

                <ToolTip placement="right" title='Система'>
                    <NavLink to='/lk/systems' className={({ isActive }) => isActive ? `${css.nav_button} ${css.nav_button_active}` : `${css.nav_button}`}>
                        <IconServer width='20px' height='20px' />
                    </NavLink>
                </ToolTip>
                <OcrSettingsLink className={css.nav_button} activeClassName={css.nav_button_active} />
            </Flex>
        )

        return <>
            <div className={`${css['menu_nav_container']}`}>
                <button className={css['nav_button']} onClick={() => handleOpenDrawerMenu(true)}>
                    <IconMenu width='20px' height='20px' />
                </button>
            </div>

            {(windowSize.width <= 500) &&
                <Divider id="hidden" variant='dark' size='1.2em' type={(windowSize.width <= 500) ? 'vertical' : 'horizontal'} />
            }
            

            <div className={css['nav_footer_content']}>
                <StorageWarningNav warnings={storageWarnings} />

                <ToolTip placement="right" title='Мой профиль'>
                    <NavLink to='/lk/profile' className={({ isActive }) => isActive ? `${css.nav_button} ${css.nav_button_active}` : `${css.nav_button}`}>
                        <IconProfile width='20px' height='20px' />
                    </NavLink>
                </ToolTip>

                <Popover content={popoverSettingsContent} trigger={deviceType === 'mobile' ? 'click' : 'hover'} arrow={{ pointAtCenter: true }} placement={deviceType === 'mobile' ? 'top' : 'rightBottom'}>
                    <button className={isSettingsPage ? `${css.nav_button} ${css.nav_button_active}` : css.nav_button}>
                        <IconSettings width='20px' height='20px' />
                    </button>
                </Popover>

                <ToolTip placement="right" title='Выход'>
                    <button className={css['nav_button']}
                        onClick={() => handleOpenModalConfirm(true, 'Выход из системы...', 'Вы действительно хотите выйти из приложения?')}>
                        <IconExitApp width='20px' height='20px' 
                    /></button>
                </ToolTip>
            </div>
        </>
    }

    /** Всплывающее окно для итоговых работ */
    const popoverFinalExaminationContent = (
        <Flex gap="8px">
            <ToolTip placement="right" title='Итоговые работы'>
                <NavLink to='/lk/finalExamination' className={({ isActive }) => isActive ? `${css.nav_button} ${css.nav_button_active}` : `${css.nav_button}`}>
                    <IconJobs width='20px' height='20px' />
                </NavLink>
            </ToolTip>

            <ToolTip placement="right" title='Архив работ'>
                <NavLink to='/lk/archive' className={({ isActive }) => isActive ? `${css.nav_button} ${css.nav_button_active}` : `${css.nav_button}`}>
                    <IconArchive width='20px' height='20px' />
                </NavLink>
            </ToolTip>

            <ToolTip placement="right" title='Печать'>
                <NavLink to='/lk/prints' className={({ isActive }) => isActive ? `${css.nav_button} ${css.nav_button_active}` : `${css.nav_button}`}>
                    <IconDiplom width='20px' height='20px' />
                </NavLink>
            </ToolTip>
        </Flex>
    )

    /** Всплывающее окно для доставки */
    const popoverDeliveryContent = (
        <Flex gap="8px">
            <ToolTip placement="right" title='Доставка'>
                <NavLink to='/lk/finality' className={({ isActive }) => isActive ? `${css.nav_button} ${css.nav_button_active}` : `${css.nav_button}`}>
                    <IconDelivery width='20px' height='20px' />
                </NavLink>
            </ToolTip>

            <ToolTip placement="right" title='Извещения о платежах'>
                <NavLink to='/lk/paymentNotifications' className={({ isActive }) => isActive ? `${css.nav_button} ${css.nav_button_active}` : `${css.nav_button}`}>
                    <IconPayment width='20px' height='20px' />
                </NavLink>
            </ToolTip>

            <ToolTip placement="right" title='Адрес - не подтверждённые'>
                <NavLink to='/lk/addressNotConfirmed' className={({ isActive }) => isActive ? `${css.nav_button} ${css.nav_button_active}` : `${css.nav_button}`}>
                    <IconAddress width='20px' height='20px' />
                </NavLink>
            </ToolTip>
        </Flex>
    )

    return <>
        <div className={css['container']}>
            <div ref={setPageHeaderPortalTarget} className={css['page_header']} />
            <Container className={css['nav_panel']} />

            <div className={css['nav_container']}>
                <Container className={css['mobile_nav']}>
                    {getNavFooter()}
                </Container>

                <div className={css['nav_content']}>
                    <div className={css['nav_content__inset']}>
                        <ToolTip placement="right" title='Мониторинг'>
                            <NavLink to='/lk/dashboard' className={({ isActive }) => isActive ? `${css.nav_button} ${css.nav_button_active}` : `${css.nav_button}`}>
                                <IconStatistics width='20px' height='20px' />
                            </NavLink>
                        </ToolTip>

                        <div role='separator' className={css['nav_divider']} />

                        <ToolTip placement="right" title='Заявки'>
                            <NavLink to='/lk/applications' onClick={() => sectioName.setValue('Заявки')} className={({ isActive }) => isActive ? `${css.nav_button} ${css.nav_button_active}` : `${css.nav_button}`}>
                                <IconApplications width='20px' height='20px' />
                                <Badge bottom={Number(countNewApplications) > 0 ? '15px' : '-3px'} right='-3px' background='#DC1C2E' isVisible={Number(countDelayedActionExpiredApplications) > 0}>{countDelayedActionExpiredApplications}</Badge>
                                <Badge bottom='-3px' right='-3px' isVisible={Number(countNewApplications) > 0}>{countNewApplications}</Badge>
                            </NavLink>
                        </ToolTip>

                        <ToolTip placement="right" title='Договора на подпись'>
                            <NavLink to='/lk/signature' className={({ isActive }) => isActive ? `${css.nav_button} ${css.nav_button_active}` : `${css.nav_button}`}>
                                <IconSignature width='20px' height='20px' />
                            </NavLink>
                        </ToolTip>

                        <ToolTip placement="right" title='Ком. предложения'>
                            <NavLink to='/lk/commercialOffers' className={({ isActive }) => isActive ? `${css.nav_button} ${css.nav_button_active}` : `${css.nav_button}`}>
                                <IconKP width='20px' height='20px' />
                            </NavLink>
                        </ToolTip>

                        <div role='separator' className={css['nav_divider']} />

                        <ToolTip placement="right" title='Контрагенты'>
                            <NavLink to='/lk/companies' className={({ isActive }) => isActive ? `${css.nav_button} ${css.nav_button_active}` : `${css.nav_button}`}>
                                <IconEntity width='20px' height='20px' />
                            </NavLink>
                        </ToolTip>

                        <ToolTip placement="right" title='Слушатели'>
                            <NavLink to='/lk/listeners' className={({ isActive }) => isActive ? `${css.nav_button} ${css.nav_button_active}` : `${css.nav_button}`}>
                                <IconListeners width='20px' height='20px' />
                            </NavLink>
                        </ToolTip>

                        <ToolTip placement="right" title='Договоры'>
                            <NavLink to='/lk/contracts' className={({ isActive }) => isActive ? `${css.nav_button} ${css.nav_button_active}` : `${css.nav_button}`}>
                                <IconContracts width='20px' height='20px' />
                            </NavLink>
                        </ToolTip>

                        <div role='separator' className={css['nav_divider']} />

                        <Popover content={popoverFinalExaminationContent} trigger={deviceType === 'mobile' ? 'click' : 'hover'} arrow={{ pointAtCenter: true }} placement={deviceType === 'mobile' ? 'top' : 'rightBottom'}>
                            <button className={isFinalExaminationPage ? `${css.nav_button} ${css.nav_button_active}` : css.nav_button}>
                                <IconJobs width='20px' height='20px' />
                            </button>
                        </Popover>

                        <Popover content={popoverDeliveryContent} trigger={deviceType === 'mobile' ? 'click' : 'hover'} arrow={{ pointAtCenter: true }} placement={deviceType === 'mobile' ? 'top' : 'rightBottom'}>
                            <button className={isDeliveryPage ? `${css.nav_button} ${css.nav_button_active}` : css.nav_button}>
                                <IconDelivery width='20px' height='20px' />
                            </button>
                        </Popover>
                    </div>
                </div>

                <div className={css['nav_footer']}>
                    {getNavFooter()}
                </div>
            </div>

            <div className={css['main_container']}>
                <PageHeaderPortalContext.Provider value={pageHeaderPortalTarget}>
                    <MainRoutes />
                </PageHeaderPortalContext.Provider>
            </div>
        </div>
    </>
}
